import {
    Button,
    ButtonGroup,
    Card,
    CardContent,
    Checkbox,
    FormControl,
    Grid,
    MenuItem,
    Select,
    Skeleton,
    Stack,
    TextField,
    Theme,
    Typography,
    useMediaQuery,
} from '@mui/material';
import moment from 'moment';
import { useDialog } from 'muibox';
import { useEffect, useRef, useState } from 'react';
import useInventory, { ContentInventory, InventoryVersion } from '../../../../hooks/useInventory';
import useMessageUsersCurrentSessions from '../../../../hooks/useMessageUsersCurrentSessions';
import useSextforceCaptions from '../../../../hooks/useSextforceCaptions';
import { OnlyFansSubscriber, OnlyFansSubscriptionsUserType } from '../../../../hooks/useSubscriber';
import { OnlyFansVaultMedias } from '../../../../hooks/useSubscriberVaultAlbums';
import useSubscribersVaultMediasAdditonal from '../../../../hooks/useSubscriberVaultMediasAdditional';
import { OnlyFansLists } from '../../../../types/onlyFansTypes';
import { isSextforceActive, parseOnlyFansListId, stripHtml } from '../../../../utils/common';
import InventoryTemplateTagsSelector from '../../../common/InventoryTemplateTagsSelector';
import InventoryTemplateVariantsSelector from '../../../common/InventoryTemplateVariantsSelector';
import TemplateModeToggle, { TemplateSubMode } from '../../../common/TemplateModeToggle';
import OnlyFansAlbumSelector from '../../../common/OnlyFansAlbumSelector';
import QuickSelectInventory from '../../../inventory/QuickSelectInventory';
import SextforceCaptionsDialog from '../../sextforce/SextforceCaptionsDialog';
import MessageUsersScheduledList from '../scheduled/MessageUsersScheduledList';
import MessageUsersAutoUnsendTimer from './MessageUsersAutoUnsendTimer';
import MessageUsersCurrentSession from './MessageUsersCurrentSession';
import MessageUsersDelayBetweenMessages from './MessageUsersDelayBetweenMessages';
import MessageUsersFormAddMedias from './MessageUsersFormAddMedias';
import MessageUsersFormEditor from './MessageUsersFormEditor';
import MessageUsersFormExclude from './MessageUsersFormExclude';
import MessageUsersFormTargetList from './MessageUsersFormTargetList';
import MessageUsersFormTargetRecentFans from './MessageUsersFormTargetRecentFans';
import MessageUsersFormTargetRecentSpenders from './MessageUsersFormTargetRecentSpenders';
import MessageUsersFormTargetUsers from './MessageUsersFormTargetUsers';
import MessageUsersPriceLock from './MessageUsersPriceLock';
import MessageUsersSchedule from './MessageUsersSchedule';
import MessageUsersTagCreators from './MessageUsersTagCreators';

export type MessageUsersContentType = 'manual' | 'album' | 'template' | 'caption';

export type MessageUsersAlbumContentTypes = { image: boolean; video: boolean; audio: boolean; gif: boolean };

type MessageUsersFormProps = {
    subscriber: OnlyFansSubscriber;
    session?: any;
    unitCost: number;
    loading: boolean;
    lists: OnlyFansLists.List[] | void | undefined;
    listsLoading: boolean;
    refetchLists: () => void;

    name: string;
    setName: (name: string) => void;
    scheduleMessage: boolean;
    setScheduleMessage: (scheduleMessage: boolean) => void;
    scheduleMessageDate: Date | null;
    setScheduleMessageDate: (date: Date | null) => void;
    colorId?: string;
    setColorId: (colorId: string | undefined) => void;
    repeatMessage: boolean;
    setRepeatMessage: (repeatMessage: boolean) => void;
    repeatEveryAmount: number;
    setRepeatEveryAmount: (amount: number) => void;
    repeatEveryUnit: string;
    setRepeatEveryUnit: (unit: string) => void;
    repeatEndDate: Date | null;
    setRepeatEndDate: (date: Date | null) => void;

    targetUsers: OnlyFansSubscriptionsUserType;
    setTargetUsers: (targetUsers: OnlyFansSubscriptionsUserType) => void;
    targetLists: { id: number | string | OnlyFansLists.Type; name: string }[];
    setTargetLists: (targetLists: { id: number | string | OnlyFansLists.Type; name: string }[]) => void;
    targetRecentSpendersDateFrom: Date | null;
    setTargetRecentSpendersDateFrom: (date: Date | null) => void;
    targetRecentSpendersDateTo: Date | null;
    setTargetRecentSpendersDateTo: (date: Date | null) => void;
    targetRecentFansDateFrom: Date | null;
    setTargetRecentFansDateFrom: (date: Date | null) => void;
    targetRecentFansDateTo: Date | null;
    setTargetRecentFansDateTo: (date: Date | null) => void;

    excludeCreators: boolean;
    setExcludeCreators: (excludeCreators: boolean) => void;

    excludeUnreadChats: boolean;
    setExcludeUnreadChats: (excludeUnreadChats: boolean) => void;

    excludeLimitMessage: boolean;
    setExcludeLimitMessage: (excludeLimitMessage: boolean) => void;
    excludeLimitMessageCount: number;
    setExcludeLimitMessageCount: (count: number) => void;

    excludeUsersList: boolean;
    setExcludeUsersList: (excludeUsersList: boolean) => void;
    excludedLists: { id: number | OnlyFansLists.Type; name: string }[];
    setExcludedLists: (excludedLists: { id: number | OnlyFansLists.Type; name: string }[]) => void;

    excludeUsersSpent: boolean;
    setExcludeUsersSpent: (excludeUsersSpent: boolean) => void;
    excludeSpentCondition: string;
    setExcludeSpentCondition: (condition: string) => void;
    excludeSpentAmount: number;
    setExcludeSpentAmount: (amount: number) => void;

    excludeUsersSpentPeriod: boolean;
    setExcludeUsersSpentPeriod: (excludeUsersSpentPeriod: boolean) => void;
    excludeSpentPeriod: string;
    setExcludeSpentPeriod: (period: string) => void;
    excludeSpentPeriodAmount: number;
    setExcludeSpentPeriodAmount: (amount: number) => void;

    excludeUsersMessagedIn: boolean;
    setExcludeUsersMessagedIn: (excludeUsersMessagedIn: boolean) => void;
    excludeUsersMessagedInPeriod: string;
    setExcludeUsersMessagedInPeriod: (period: string) => void;
    excludeUsersMessagedInPeriodAmount: number;
    setExcludeUsersMessagedInPeriodAmount: (amount: number) => void;

    excludeFans: boolean;
    setExcludeFans: (excludeFans: boolean) => void;

    messageType: MessageUsersContentType;
    setMessageType: (messageType: MessageUsersContentType) => void;
    sourceAlbumId: number;
    setSourceAlbumId: (sourceAlbumId: number) => void;
    albumContentTypes: MessageUsersAlbumContentTypes;
    setAlbumContentTypes: (albumContentTypes: MessageUsersAlbumContentTypes) => void;
    albumUseRandomCaption: boolean;
    setAlbumUseRandomCaption: (albumUseRandomCaption: boolean) => void;
    captionCategory: string;
    setCaptionCategory: (captionCategory: string) => void;
    templateIds: { inventoryId: number; versionId: number }[];
    setTemplateIds: (templateIds: { inventoryId: number; versionId: number }[]) => void;
    templateTags: string[];
    setTemplateTags: (templateTags: string[]) => void;
    templateTagsPaidOnly: boolean;
    setTemplateTagsPaidOnly: (templateTagsPaidOnly: boolean) => void;

    includeMedia: boolean;
    setIncludeMedia: (includeMedia: boolean) => void;
    medias: OnlyFansVaultMedias.RestructuredResponseItem[];
    setMedias: React.Dispatch<React.SetStateAction<OnlyFansVaultMedias.RestructuredResponseItem[]>>;
    thumbnails: any[];
    mediasPreviews: number[];
    setMediasPreviews: React.Dispatch<React.SetStateAction<number[]>>;

    setThumbnails: React.Dispatch<React.SetStateAction<any[]>>;
    getThumbnail: (thumbId: number, thumbUrl: string) => void;
    getAudio: (url: string) => Promise<Blob | null>;

    tagCreators: boolean;
    setTagCreators: (tagCreators: boolean) => void;
    taggedCreators: { id: number; name: string }[];
    setTaggedCreators: React.Dispatch<React.SetStateAction<{ id: number; name: string }[]>>;
    taggedReleaseForms: { id: number; name: string }[];
    setTaggedReleaseForms: React.Dispatch<React.SetStateAction<{ id: number; name: string }[]>>;

    lockMessage: boolean;
    setLockMessage: (lockMessage: boolean) => void;
    lockMessagePrice: number;
    setLockMessagePrice: (price: number) => void;
    lockedText: boolean;
    setLockedText: (lockedText: boolean) => void;

    autoUnsendTimer: boolean;
    setAutoUnsendTimer: (autoUnsendMessage: boolean) => void;
    autoUnsendTimerPeriodAmount: number;
    setAutoUnsendTimerPeriodAmount: (autoUnsendTimerPeriodAmount: number) => void;

    delayBetweenMessages: number;
    setDelayBetweenMessages: (delayBetweenMessages: number) => void;

    message: string;
    setMessage: (message: string) => void;
    messageHasRestrictedWords: boolean;
    messageFoundRestrictedWords: string[];
    checkForRestrictedWords: (text: string) => void;
    usernameReplacement: string;
    setUsernameReplacement: (usernameReplacement: string) => void;
    saveTemplate: boolean;
    setSaveTemplate: (saveTemplate: boolean) => void;
    saveTemplateName: string;
    setSaveTemplateName: (templateName: string) => void;

    isSendingMessage: boolean;
    sendMessage: () => void;
    resetForm: () => void;
    theme: Theme;
    edit?: boolean;
    isInsideDialog?: boolean;
};

const MessageUsersForm = (props: MessageUsersFormProps) => {
    const {
        subscriber,
        session,
        unitCost,
        lists,
        listsLoading,
        refetchLists,
        name,
        setName,
        scheduleMessage,
        setScheduleMessage,
        scheduleMessageDate,
        setScheduleMessageDate,
        colorId,
        setColorId,
        repeatMessage,
        setRepeatMessage,
        repeatEveryAmount,
        setRepeatEveryAmount,
        repeatEveryUnit,
        setRepeatEveryUnit,
        repeatEndDate,
        setRepeatEndDate,
        targetUsers,
        setTargetUsers,
        targetLists,
        setTargetLists,
        targetRecentSpendersDateFrom,
        setTargetRecentSpendersDateFrom,
        targetRecentSpendersDateTo,
        setTargetRecentSpendersDateTo,
        targetRecentFansDateFrom,
        setTargetRecentFansDateFrom,
        targetRecentFansDateTo,
        setTargetRecentFansDateTo,
        excludeCreators,
        setExcludeCreators,
        excludeUnreadChats,
        setExcludeUnreadChats,
        excludeLimitMessage,
        setExcludeLimitMessage,
        excludeLimitMessageCount,
        setExcludeLimitMessageCount,
        excludeUsersList,
        setExcludeUsersList,
        excludedLists,
        setExcludedLists,
        excludeUsersSpent,
        setExcludeUsersSpent,
        excludeSpentCondition,
        setExcludeSpentCondition,
        excludeSpentAmount,
        setExcludeSpentAmount,
        excludeUsersSpentPeriod,
        setExcludeUsersSpentPeriod,
        excludeSpentPeriod,
        setExcludeSpentPeriod,
        excludeSpentPeriodAmount,
        setExcludeSpentPeriodAmount,
        excludeUsersMessagedIn,
        setExcludeUsersMessagedIn,
        excludeUsersMessagedInPeriod,
        setExcludeUsersMessagedInPeriod,
        excludeUsersMessagedInPeriodAmount,
        setExcludeUsersMessagedInPeriodAmount,
        excludeFans,
        setExcludeFans,
        loading,
        messageType,
        setMessageType,
        sourceAlbumId,
        setSourceAlbumId,
        albumContentTypes,
        setAlbumContentTypes,
        albumUseRandomCaption,
        setAlbumUseRandomCaption,
        captionCategory,
        setCaptionCategory,
        templateIds,
        setTemplateIds,
        templateTags,
        setTemplateTags,
        templateTagsPaidOnly,
        setTemplateTagsPaidOnly,
        includeMedia,
        setIncludeMedia,
        medias,
        setMedias,
        thumbnails,
        mediasPreviews,
        setMediasPreviews,
        setThumbnails,
        getThumbnail,
        getAudio,
        tagCreators,
        setTagCreators,
        taggedCreators,
        setTaggedCreators,
        taggedReleaseForms,
        setTaggedReleaseForms,
        lockMessage,
        setLockMessage,
        lockMessagePrice,
        setLockMessagePrice,
        lockedText,
        setLockedText,
        autoUnsendTimer,
        setAutoUnsendTimer,
        autoUnsendTimerPeriodAmount,
        setAutoUnsendTimerPeriodAmount,
        delayBetweenMessages,
        setDelayBetweenMessages,
        message,
        setMessage,
        messageHasRestrictedWords,
        messageFoundRestrictedWords,
        checkForRestrictedWords,
        usernameReplacement,
        setUsernameReplacement,
        saveTemplate,
        setSaveTemplate,
        saveTemplateName,
        setSaveTemplateName,
        isSendingMessage,
        sendMessage,
        resetForm,
        theme,
        edit,
        isInsideDialog,
    } = props;
    const dialog = useDialog();

    const sendMeessageHeaderRef = useRef<HTMLDivElement>(null);

    const { isMessageUsersSendSessionRunning, isMessageUsersSendJobInQueue } = useMessageUsersCurrentSessions();

    const [templateSubMode, setTemplateSubMode] = useState<TemplateSubMode>((templateTags?.length ?? 0) > 0 ? 'tags' : 'ids');
    const [vaultAlbum, setVaultAlbum] = useState<number>(-1);
    const [vaultAlbumName, setVaultAlbumName] = useState<string | undefined>();
    const [selectedInventoryVersion, setSelectedInventoryVersion] = useState<InventoryVersion>();
    const [selectedInventory, setSelectedInventory] = useState<ContentInventory>();
    const [showCaptionsDialog, setShowCaptionsDialog] = useState<boolean>(false);
    const { getManyVaultMediaFromDatabase } = useSubscribersVaultMediasAdditonal();

    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { categories: captionCategories = [] } = useSextforceCaptions(
        messageType === 'caption' || (messageType === 'album' && albumUseRandomCaption),
    );
    const { data: contentInventory, isLoading: isContentInventoryLoading } = useInventory();

    useEffect(() => {
        const switchInventory = async () => {
            if (messageType !== 'manual') {
                return;
            }

            if (selectedInventoryVersion) {
                setLockMessage(false);
                setLockMessagePrice(0);
                setTagCreators(false);
                setTaggedCreators([]);
                setTaggedReleaseForms([]);
                setIncludeMedia(false);
                setMedias([]);
                setMediasPreviews([]);
                setExcludeUsersList(false);

                if (selectedInventoryVersion.excludeLists?.length ?? 0 > 0) {
                    setExcludeUsersList(true);
                    setExcludedLists(
                        selectedInventoryVersion.excludeLists?.map(items => {
                            if (typeof items.listId === 'number') {
                                return {
                                    id: items.listId,
                                    name:
                                        lists?.find(list => parseOnlyFansListId(list.id) === parseOnlyFansListId(items.listId))?.name ||
                                        'Unknown',
                                };
                            }
                            return {
                                id: items.listId as OnlyFansLists.Type,
                                name:
                                    lists?.find(list => parseOnlyFansListId(list.id) === parseOnlyFansListId(items.listId))?.name ||
                                    'Unknown',
                            };
                        }) ?? [],
                    );
                }

                setName(`${selectedInventory ? `${selectedInventory.name} - ` : ''}${selectedInventoryVersion.versionName || ''}`);

                // setTemplateText(selectedInventoryVersion.text);
                setMessage(selectedInventoryVersion.text);

                if (Number(selectedInventoryVersion.price) > 0) {
                    setLockMessage(true);
                    setLockMessagePrice(Number(selectedInventoryVersion.price));
                }

                if (selectedInventoryVersion.creatorTags) {
                    setTagCreators(true);
                    setTaggedCreators(
                        selectedInventoryVersion.creatorTags.map(item => ({ id: item.creatorOnlyfansId, name: item.creatorName })),
                    );
                }

                if (
                    selectedInventoryVersion.additionalSettings?.consentForms &&
                    selectedInventoryVersion.additionalSettings.consentForms.length > 0
                ) {
                    setTagCreators(true);
                    setTaggedReleaseForms(
                        selectedInventoryVersion.additionalSettings.consentForms.map(item => ({ id: item.id, name: item.name })),
                    );
                }

                // Auto-unsend after a number of hours, only when the template defines a positive value.
                // The unsend timer on this screen is always expressed in hours.
                if (
                    selectedInventoryVersion.additionalSettings?.autoUnsendHours &&
                    selectedInventoryVersion.additionalSettings.autoUnsendHours > 0
                ) {
                    setAutoUnsendTimer(true);
                    setAutoUnsendTimerPeriodAmount(selectedInventoryVersion.additionalSettings.autoUnsendHours);
                }

                if (
                    (selectedInventoryVersion.unlockedMedias?.length ?? 0) > 0 ||
                    (selectedInventoryVersion.lockedMedias?.length ?? 0) > 0
                ) {
                    setIncludeMedia(true);

                    const grabMedias = await getManyVaultMediaFromDatabase([
                        ...(selectedInventoryVersion.lockedMedias?.map(item => item.mediaId) ?? []),
                        ...(selectedInventoryVersion.unlockedMedias?.map(item => item.mediaId) ?? []),
                    ]);

                    setMedias(grabMedias);

                    // Preview media only makes sense for a paid (locked) message. If the inventory
                    // item has no price, include the media but don't mark any of it as preview.
                    if (
                        Number(selectedInventoryVersion.price) > 0 &&
                        (selectedInventoryVersion.unlockedMedias?.length ?? 0) > 0
                    ) {
                        setLockMessage(true);
                        setMediasPreviews(selectedInventoryVersion.unlockedMedias?.map(item => item.mediaId) ?? []);
                    }
                }
            }
        };
        switchInventory();
    }, [selectedInventoryVersion]);

    const rawMessage = stripHtml(message);

    const isNotValid: boolean =
        (scheduleMessage &&
            (!name ||
                name.trim() === '' ||
                !scheduleMessageDate ||
                !moment(scheduleMessageDate).isValid() ||
                !moment(scheduleMessageDate).isAfter(moment()))) ||
        (repeatMessage && repeatEveryAmount === 0) ||
        (excludeUsersList && (!excludedLists || excludedLists.length === 0)) ||
        (excludeLimitMessage && excludeLimitMessageCount === 0) ||
        (excludeUsersSpentPeriod && excludeSpentPeriodAmount === 0) ||
        (messageType === 'manual' &&
            ((tagCreators && taggedCreators.length === 0 && taggedReleaseForms.length === 0) ||
                (lockMessage && lockMessagePrice < 3) ||
                (lockMessage && !lockedText && medias.length === 0) ||
                (rawMessage.trim().length === 0 && medias.length === 0 && lockMessage) ||
                (rawMessage.trim().length === 0 && !lockMessage && medias.length === 0) ||
                (messageHasRestrictedWords && isSextforceActive(subscriber) && subscriber.sextforce?.bigBrother?.active) ||
                (saveTemplate && (!saveTemplateName || saveTemplateName.trim().length === 0)) ||
                (includeMedia && (medias.length === 0 || medias.length > 40)))) ||
        (messageType === 'album' &&
            (sourceAlbumId <= 0 ||
                !(albumContentTypes.image || albumContentTypes.video || albumContentTypes.audio || albumContentTypes.gif))) ||
        (messageType === 'template' &&
            (templateSubMode === 'ids' ? templateIds.length === 0 : templateTags.length === 0)) ||
        (autoUnsendTimer &&
            (messageType !== 'manual' || !lockMessage || (lockMessage && !lockMessagePrice)) &&
            (autoUnsendTimerPeriodAmount === 0 || autoUnsendTimerPeriodAmount > 24)) ||
        (targetUsers === 'lists' && targetLists.length === 0) ||
        (targetUsers === 'recentSpenders' && (!targetRecentSpendersDateFrom || !targetRecentSpendersDateTo)) ||
        isSendingMessage;

    return (
        <>
            <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent="center" sx={{ marginBottom: theme.spacing(2) }}>
                <Grid item xs={12} md={isInsideDialog ? 12 : 6}>
                    {!isInsideDialog && (isMessageUsersSendSessionRunning || isMessageUsersSendJobInQueue) && (
                        <MessageUsersCurrentSession />
                    )}

                    {!edit && <MessageUsersScheduledList />}

                    {!edit && (
                        <Typography
                            ref={sendMeessageHeaderRef}
                            variant="h5"
                            sx={{ marginTop: theme.spacing(2), marginBottom: theme.spacing(2) }}
                        >
                            Send Message
                        </Typography>
                    )}

                    {loading ? (
                        <Skeleton />
                    ) : (
                        <>
                            <MessageUsersFormTargetUsers
                                targetUsers={targetUsers}
                                setTargetUsers={setTargetUsers}
                                unitCost={unitCost}
                                session={session}
                            />

                            {targetUsers === 'lists' && (
                                <MessageUsersFormTargetList targetLists={targetLists} setTargetLists={setTargetLists} session={session} />
                            )}

                            {targetUsers === 'recentSpenders' && (
                                <MessageUsersFormTargetRecentSpenders
                                    usersType={targetUsers}
                                    initialDateFrom={edit ? targetRecentSpendersDateFrom : undefined}
                                    initialDateTo={edit ? targetRecentSpendersDateTo : undefined}
                                    targetRecentSpendersDateFrom={targetRecentSpendersDateFrom}
                                    setTargetRecentSpendersDateFrom={setTargetRecentSpendersDateFrom}
                                    targetRecentSpendersDateTo={targetRecentSpendersDateTo}
                                    setTargetRecentSpendersDateTo={setTargetRecentSpendersDateTo}
                                    session={session}
                                />
                            )}

                            {targetUsers === 'recentFans' && (
                                <MessageUsersFormTargetRecentFans
                                    usersType={targetUsers}
                                    targetRecentFansDateFrom={targetRecentFansDateFrom}
                                    setTargetRecentFansDateFrom={setTargetRecentFansDateFrom}
                                    targetRecentFansDateTo={targetRecentFansDateTo}
                                    setTargetRecentFansDateTo={setTargetRecentFansDateTo}
                                    session={session}
                                />
                            )}

                            <MessageUsersFormExclude
                                lists={lists}
                                listsLoading={listsLoading}
                                refetchLists={refetchLists}
                                targetUsers={targetUsers}
                                excludeCreators={excludeCreators}
                                setExcludeCreators={setExcludeCreators}
                                excludeUnreadChats={excludeUnreadChats}
                                setExcludeUnreadChats={setExcludeUnreadChats}
                                excludeLimitMessage={excludeLimitMessage}
                                setExcludeLimitMessage={setExcludeLimitMessage}
                                excludeLimitMessageCount={excludeLimitMessageCount}
                                setExcludeLimitMessageCount={setExcludeLimitMessageCount}
                                excludeUsersList={excludeUsersList}
                                setExcludeUsersList={setExcludeUsersList}
                                excludedLists={excludedLists}
                                setExcludedLists={setExcludedLists}
                                excludeUsersSpent={excludeUsersSpent}
                                setExcludeUsersSpent={setExcludeUsersSpent}
                                excludeSpentCondition={excludeSpentCondition}
                                setExcludeSpentCondition={setExcludeSpentCondition}
                                excludeSpentAmount={excludeSpentAmount}
                                setExcludeSpentAmount={setExcludeSpentAmount}
                                excludeUsersSpentPeriod={excludeUsersSpentPeriod}
                                setExcludeUsersSpentPeriod={setExcludeUsersSpentPeriod}
                                excludeSepntPeriod={excludeSpentPeriod}
                                setExcludeSpentPeriod={setExcludeSpentPeriod}
                                excludeSpentPeriodAmount={excludeSpentPeriodAmount}
                                setExcludeSpentPeriodAmount={setExcludeSpentPeriodAmount}
                                excludeUsersMessagedIn={excludeUsersMessagedIn}
                                setExcludeUsersMessagedIn={setExcludeUsersMessagedIn}
                                excludeUsersMessagedInPeriod={excludeUsersMessagedInPeriod}
                                setExcludeUsersMessagedInPeriod={setExcludeUsersMessagedInPeriod}
                                excludeUsersMessagedInPeriodAmount={excludeUsersMessagedInPeriodAmount}
                                setExcludeUsersMessagedInPeriodAmount={setExcludeUsersMessagedInPeriodAmount}
                                excludeFans={excludeFans}
                                setExcludeFans={setExcludeFans}
                                session={session}
                                theme={theme}
                            />
                            <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent="center" sx={{ marginBottom: 1 }}>
                                <Grid item xs={12}>
                                    <Card variant="elevation">
                                        <CardContent>
                                            <Grid container spacing={2} flexGrow={0} alignItems="center">
                                                <Grid item xs={12}>
                                                    <Typography variant="inherit">Message Content</Typography>
                                                    <Typography variant="body2" color="text.secondary" marginTop={0.5}>
                                                        Compose the message manually, send random media from a vault album, use templates
                                                        from content inventory, or send a random caption.
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <ButtonGroup fullWidth orientation={isMobile ? 'vertical' : 'horizontal'}>
                                                        <Button
                                                            variant={messageType === 'manual' ? 'contained' : 'outlined'}
                                                            color="primary"
                                                            size="large"
                                                            disabled={!!session}
                                                            onClick={() => setMessageType('manual')}
                                                        >
                                                            Manual
                                                        </Button>
                                                        <Button
                                                            variant={messageType === 'album' ? 'contained' : 'outlined'}
                                                            color="primary"
                                                            size="large"
                                                            disabled={!!session}
                                                            onClick={() => setMessageType('album')}
                                                        >
                                                            Album
                                                        </Button>
                                                        <Button
                                                            variant={messageType === 'template' ? 'contained' : 'outlined'}
                                                            color="primary"
                                                            size="large"
                                                            disabled={!!session}
                                                            onClick={() => setMessageType('template')}
                                                        >
                                                            Template
                                                        </Button>
                                                        <Button
                                                            variant={messageType === 'caption' ? 'contained' : 'outlined'}
                                                            color="primary"
                                                            size="large"
                                                            disabled={!!session}
                                                            onClick={() => setMessageType('caption')}
                                                        >
                                                            Caption
                                                        </Button>
                                                    </ButtonGroup>
                                                </Grid>

                                                {messageType === 'album' && (
                                                    <>
                                                        <Grid item xs={12}>
                                                            Source Album
                                                            <Typography variant="body2" color="text.secondary" marginTop={0.5}>
                                                                Random media is chosen from this album each time a message session is sent.
                                                                Album messages are always sent free.
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <OnlyFansAlbumSelector
                                                                size="small"
                                                                vaultAlbum={sourceAlbumId}
                                                                handleAlbumChange={e => {
                                                                    const v = e.target.value;
                                                                    setSourceAlbumId(v === '' ? 0 : Number.parseInt(v, 10));
                                                                }}
                                                                disabled={!!session}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            Content to Include
                                                            <Typography variant="body2" color="text.secondary" marginTop={0.5}>
                                                                Which types of content from the album should be included?
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <Stack direction="column" flexWrap="wrap" gap={0} useFlexGap>
                                                                {(
                                                                    [
                                                                        { key: 'image', label: 'Images' },
                                                                        { key: 'video', label: 'Videos' },
                                                                        { key: 'audio', label: 'Audio' },
                                                                        { key: 'gif', label: 'GIFs' },
                                                                    ] as const
                                                                ).map(contentTypeOption => (
                                                                    <FormControl
                                                                        key={contentTypeOption.key}
                                                                        component="fieldset"
                                                                        sx={{ mt: 0, mb: 0 }}
                                                                    >
                                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                                            <Checkbox
                                                                                size="medium"
                                                                                checked={albumContentTypes[contentTypeOption.key]}
                                                                                disabled={!!session}
                                                                                onChange={e =>
                                                                                    setAlbumContentTypes({
                                                                                        ...albumContentTypes,
                                                                                        [contentTypeOption.key]: e.target.checked,
                                                                                    })
                                                                                }
                                                                            />
                                                                            <Typography variant="body2">
                                                                                {contentTypeOption.label}
                                                                            </Typography>
                                                                        </Stack>
                                                                    </FormControl>
                                                                ))}
                                                            </Stack>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <FormControl component="fieldset" sx={{ mt: 0 }}>
                                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                                    <Checkbox
                                                                        size="medium"
                                                                        checked={albumUseRandomCaption}
                                                                        disabled={!!session}
                                                                        onChange={e => setAlbumUseRandomCaption(e.target.checked)}
                                                                    />
                                                                    <Typography variant="body2">Random Caption</Typography>
                                                                </Stack>
                                                            </FormControl>
                                                        </Grid>
                                                    </>
                                                )}

                                                {messageType === 'template' && (
                                                    <>
                                                        <Grid item xs={12}>
                                                            Templates
                                                            <Typography variant="body2" color="text.secondary" marginTop={0.5}>
                                                                Select one or more templates from content inventory, or select one or more
                                                                template tags to draw from every template carrying any of those tags. One
                                                                matching template will be randomly chosen each time this message is sent.
                                                                The message text, media, price and tags come from the template. The
                                                                targeting selected above is used &mdash; not the template&apos;s send
                                                                lists.
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <TemplateModeToggle
                                                                value={templateSubMode}
                                                                onChange={nextMode => {
                                                                    setTemplateSubMode(nextMode);
                                                                    setTemplateIds([]);
                                                                    setTemplateTags([]);
                                                                }}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            {templateSubMode === 'ids' ? (
                                                                <InventoryTemplateVariantsSelector
                                                                    inventory={contentInventory}
                                                                    loading={isContentInventoryLoading}
                                                                    selectedTemplates={templateIds}
                                                                    onChange={setTemplateIds}
                                                                />
                                                            ) : (
                                                                <InventoryTemplateTagsSelector
                                                                    inventory={contentInventory}
                                                                    loading={isContentInventoryLoading}
                                                                    value={templateTags}
                                                                    onChange={setTemplateTags}
                                                                    paidOnly={templateTagsPaidOnly}
                                                                    onPaidOnlyChange={setTemplateTagsPaidOnly}
                                                                />
                                                            )}
                                                        </Grid>
                                                    </>
                                                )}

                                                {messageType === 'caption' && (
                                                    <Grid item xs={12}>
                                                        Caption
                                                        <Typography variant="body2" color="text.secondary" marginBottom={0.5}>
                                                            Captions are sent free. A random caption is chosen from the selected category
                                                            each time a message session is sent. These are perfect for messaging online fans
                                                            with a short, flirty message to get their attention. You can manage your
                                                            captions in the &quot;Manage Captions&quot; section below.
                                                        </Typography>
                                                    </Grid>
                                                )}

                                                {(messageType === 'caption' || (messageType === 'album' && albumUseRandomCaption)) && (
                                                    <>
                                                        <Grid item xs={12}>
                                                            <Typography variant="body2" color="text.secondary" marginBottom={0.5}>
                                                                Caption category (optional)
                                                            </Typography>
                                                            <Select
                                                                fullWidth
                                                                size="small"
                                                                value={captionCategory}
                                                                disabled={!!session}
                                                                onChange={e => setCaptionCategory(e.target.value)}
                                                            >
                                                                <MenuItem value="">All captions</MenuItem>
                                                                {captionCategories.map(category => (
                                                                    <MenuItem key={category._id} value={category.name}>
                                                                        {category.name}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                disabled={!!session}
                                                                onClick={() => setShowCaptionsDialog(true)}
                                                            >
                                                                Manage Captions
                                                            </Button>
                                                        </Grid>
                                                    </>
                                                )}

                                                {messageType !== 'manual' && (
                                                    <>
                                                        <Grid item xs={12}>
                                                            <Typography variant="body2" color="text.secondary" marginBottom={0.5}>
                                                                %USERNAME% replacement (optional)
                                                            </Typography>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                placeholder="e.g. babe"
                                                                value={usernameReplacement}
                                                                disabled={!!session}
                                                                onChange={e => setUsernameReplacement(e.target.value)}
                                                            />
                                                        </Grid>
                                                    </>
                                                )}
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>

                            {messageType === 'manual' && (
                                <Grid
                                    container
                                    spacing={1}
                                    flexGrow={0}
                                    alignItems="center"
                                    justifyContent="center"
                                    sx={{ marginBottom: 1 }}
                                >
                                    <Grid item xs={12}>
                                        <Card variant="elevation">
                                            <CardContent>
                                                <QuickSelectInventory
                                                    setSelectedInventoryVersion={setSelectedInventoryVersion}
                                                    setSelectedInventory={setSelectedInventory}
                                                />
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            )}

                            {messageType === 'manual' && (
                                <MessageUsersFormAddMedias
                                    subscriber={subscriber}
                                    serviceName="messageUsers"
                                    includeMedia={includeMedia}
                                    setIncludeMedia={setIncludeMedia}
                                    vaultAlbum={vaultAlbum}
                                    setVaultAlbum={setVaultAlbum}
                                    vaultAlbumName={vaultAlbumName}
                                    setVaultAlbumName={setVaultAlbumName}
                                    medias={medias}
                                    setMedias={setMedias}
                                    mediasPreviews={mediasPreviews}
                                    setMediasPreviews={setMediasPreviews}
                                    thumbnails={thumbnails}
                                    getThumbnail={getThumbnail}
                                    getAudio={getAudio}
                                    tagCreators={tagCreators}
                                    setTagCreators={setTagCreators}
                                    taggedCreators={taggedCreators}
                                    setTaggedCreators={setTaggedCreators}
                                    taggedReleaseForms={taggedReleaseForms}
                                    setTaggedReleaseForms={setTaggedReleaseForms}
                                    session={session}
                                />
                            )}

                            {messageType === 'manual' && includeMedia && (
                                <MessageUsersTagCreators
                                    subscriber={subscriber}
                                    tagCreators={tagCreators}
                                    setTagCreators={setTagCreators}
                                    taggedCreators={taggedCreators}
                                    setTaggedCreators={setTaggedCreators}
                                    taggedReleaseForms={taggedReleaseForms}
                                    setTaggedReleaseForms={setTaggedReleaseForms}
                                    session={session}
                                />
                            )}

                            {messageType === 'manual' && (
                                <MessageUsersPriceLock
                                    subscriber={subscriber}
                                    lockMessage={lockMessage}
                                    setLockMessage={setLockMessage}
                                    lockMessagePrice={lockMessagePrice}
                                    setLockMessagePrice={setLockMessagePrice}
                                    lockedText={lockedText}
                                    setLockedText={setLockedText}
                                    medias={medias}
                                    thumbnails={thumbnails}
                                    mediasPreviews={mediasPreviews}
                                    setMediasPreviews={setMediasPreviews}
                                    session={session}
                                />
                            )}

                            <MessageUsersAutoUnsendTimer
                                autoUnsendTimer={autoUnsendTimer}
                                setAutoUnsendTimer={setAutoUnsendTimer}
                                autoUnsendTimerPeriodAmount={autoUnsendTimerPeriodAmount}
                                setAutoUnsendTimerPeriodAmount={setAutoUnsendTimerPeriodAmount}
                                lockMessage={lockMessage}
                                lockMessagePrice={lockMessagePrice}
                                session={session}
                                theme={theme}
                            />

                            <MessageUsersDelayBetweenMessages
                                delayBetweenMessages={delayBetweenMessages}
                                setDelayBetweenMessages={setDelayBetweenMessages}
                                session={session}
                                theme={theme}
                            />

                            {messageType === 'manual' && (
                                <MessageUsersFormEditor
                                    subscriber={subscriber}
                                    message={message}
                                    setMessage={setMessage}
                                    messageHasRestrictedWords={messageHasRestrictedWords}
                                    messageFoundRestrictedWords={messageFoundRestrictedWords}
                                    checkForRestrictedWords={checkForRestrictedWords}
                                    usernameReplacement={usernameReplacement}
                                    setUsernameReplacement={setUsernameReplacement}
                                    taggedCreators={taggedCreators}
                                    taggedReleaseForms={taggedReleaseForms}
                                    lockMessage={lockMessage}
                                    lockMessagePrice={lockMessagePrice}
                                    lockedText={lockedText}
                                    saveTemplate={saveTemplate}
                                    setSaveTemplate={setSaveTemplate}
                                    saveTemplateName={saveTemplateName}
                                    setSaveTemplateName={setSaveTemplateName}
                                    borderless={false}
                                    session={session}
                                    theme={theme}
                                />
                            )}

                            <MessageUsersSchedule
                                isSessionOrJobRunning={isMessageUsersSendSessionRunning || isMessageUsersSendJobInQueue}
                                name={name}
                                setName={setName}
                                scheduleMessage={scheduleMessage}
                                setScheduleMessage={setScheduleMessage}
                                dateTime={scheduleMessageDate}
                                setDateTime={setScheduleMessageDate}
                                colorId={colorId}
                                setColorId={setColorId}
                                repeatMessage={repeatMessage}
                                setRepeatMessage={setRepeatMessage}
                                repeatEveryAmount={repeatEveryAmount}
                                setRepeatEveryAmount={setRepeatEveryAmount}
                                repeatEveryUnit={repeatEveryUnit}
                                setRepeatEveryUnit={setRepeatEveryUnit}
                                repeatEndDate={repeatEndDate}
                                setRepeatEndDate={setRepeatEndDate}
                                session={session}
                                theme={theme}
                                key={isSendingMessage || isMessageUsersSendJobInQueue ? 'yes' : 'no'}
                            />

                            {!edit && (
                                <Grid
                                    container
                                    spacing={1}
                                    flexGrow={0}
                                    alignItems="center"
                                    justifyContent="center"
                                    sx={{ marginBottom: 1 }}
                                >
                                    <Grid item xs={8}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            size="large"
                                            fullWidth
                                            disabled={isNotValid}
                                            onClick={() => {
                                                // if scheduling, make sure the schedule date is in the future
                                                if (scheduleMessage && scheduleMessageDate && scheduleMessageDate < new Date()) {
                                                    dialog.alert({
                                                        message: 'The scheduled date must be in the future!',
                                                        title: 'Error',
                                                    });

                                                    return;
                                                }

                                                dialog
                                                    .confirm({
                                                        message: `Are you sure you want to ${
                                                            scheduleMessage ? 'schedule' : 'send'
                                                        } this message?`,
                                                        title: 'Confirm',
                                                        ok: { text: scheduleMessage ? 'Schedule' : 'Send!', variant: 'contained' },
                                                        cancel: { text: 'Cancel' },
                                                    })
                                                    .then(() => {
                                                        // Send
                                                        sendMessage();
                                                    })
                                                    .catch(() => {});
                                            }}
                                        >
                                            <span>{scheduleMessage ? 'Schedule Message' : 'Send Message'}</span>
                                        </Button>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            size="large"
                                            fullWidth
                                            onClick={() => {
                                                dialog
                                                    .confirm({
                                                        message: `Are you sure you want to reset the whole form? Everything you've entered will be cleared.`,
                                                        title: 'Confirm',
                                                        ok: {
                                                            text: 'Reset',
                                                            variant: 'contained',
                                                            color: 'secondary',
                                                        },
                                                        cancel: { text: 'Cancel' },
                                                    })
                                                    .then(() => {
                                                        // Send
                                                        resetForm();

                                                        sendMeessageHeaderRef.current?.scrollIntoView({ behavior: 'smooth' });
                                                    })
                                                    .catch(() => {});
                                            }}
                                        >
                                            Reset
                                        </Button>
                                    </Grid>
                                </Grid>
                            )}
                        </>
                    )}
                </Grid>
            </Grid>

            <SextforceCaptionsDialog open={showCaptionsDialog} onClose={() => setShowCaptionsDialog(false)} />
        </>
    );
};

export default MessageUsersForm;
